import { makeApiProvider } from '@pflegenavi/shared-frontend/platform';
import { createContext } from 'react';
import type { IOnboardingApi } from './api';
import { OnboardingApi } from './api';
import type { UseMutationResult } from 'react-query';
import { useMutation, useQueryClient } from 'react-query';
import type {
  OnboardingControllerBodyTypes,
  OnboardingControllerResultTypes,
} from '@pflegenavi/service-provider-shared/api-endpoints';
import type { UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

const ApiContext = createContext<IOnboardingApi | undefined>(undefined);
const { useApi: useOnboardingApi, ApiProvider: OnboardingApiProvider } =
  makeApiProvider({
    name: 'Onboarding',
    ApiContext,
    newApi: (tenantId, auth, apiUrl) =>
      new OnboardingApi(tenantId, auth, apiUrl),
  });

export {
  useOnboardingApi,
  OnboardingApiProvider,
  ApiContext as OnboardingApiContext,
};
export type { IOnboardingApi } from './api';

const ACCEPT_INVITATION_KEY = ['accept-invitation'];
const REJECT_INVITATION_KEY = ['reject-invitation'];
const GET_NURSING_HOME_LIST_KEY = ['get-nursing-home-list'];

export const useAcceptInvitation = (): UseMutationResult<
  OnboardingControllerResultTypes['acceptInvitation'],
  unknown,
  OnboardingControllerBodyTypes['acceptInvitation']
> => {
  const api = useOnboardingApi();
  const queryClient = useQueryClient();
  const result = useMutation<
    OnboardingControllerResultTypes['acceptInvitation'],
    unknown,
    OnboardingControllerBodyTypes['acceptInvitation']
  >(
    ACCEPT_INVITATION_KEY,
    (data) =>
      api.acceptInvitation({
        body: data,
      }),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(GET_NURSING_HOME_LIST_KEY);
      },
    }
  );
  return result;
};

export const useRejectInvitation = (): UseMutationResult<
  OnboardingControllerResultTypes['rejectInvitation'],
  unknown,
  OnboardingControllerBodyTypes['rejectInvitation']
> => {
  const api = useOnboardingApi();
  const result = useMutation<
    OnboardingControllerResultTypes['rejectInvitation'],
    unknown,
    OnboardingControllerBodyTypes['rejectInvitation']
  >(REJECT_INVITATION_KEY, (data) =>
    api.rejectInvitation({
      body: data,
    })
  );
  return result;
};

export const useGetNursingHomeList = (
  options?: Omit<
    UseQueryOptions<OnboardingControllerResultTypes['getNursingHomeList']>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<OnboardingControllerResultTypes['getNursingHomeList']> => {
  const api = useOnboardingApi();
  return useQuery({
    queryKey: GET_NURSING_HOME_LIST_KEY,
    queryFn: () => {
      return api.getNursingHomeList();
    },
    ...options,
  });
};
